import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import type { McpAccess } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/misc";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { useDesk } from "@/data/context";
import { describeError } from "@/data/api";
import { qk } from "@/data/cache";
import { formatTimestamp, localZone } from "@/lib/dates";
import { Row, Section } from "./parts";

const PLACEHOLDER_PATH = "<repo>/packages/mcp/dist/desk-mcp.mjs";

/** Config for an agent's MCP client. It names the data dir; the server reads the token from there. */
export function mcpConfig(access: Pick<McpAccess, "serverPath" | "dataDir">): string {
  const server: Record<string, unknown> = { command: "node", args: [access.serverPath ?? PLACEHOLDER_PATH] };
  if (access.dataDir) server.env = { DESK_DATA_DIR: access.dataDir };
  return JSON.stringify({ mcpServers: { desk: server } }, null, 2);
}

export function McpSection() {
  const { api } = useDesk();
  const qc = useQueryClient();
  const { data: access, error } = useQuery({ queryKey: qk.mcp, queryFn: () => api.mcpAccess() });
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<McpAccess>, failure: string, success?: string) => {
    setBusy(true);
    try {
      qc.setQueryData(qk.mcp, await fn());
      if (success) toast(success);
    } catch (e) {
      toast.error(failure, { description: describeError(e) });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast("Setup copied");
    } catch {
      toast.error("Couldn't copy to the clipboard");
    }
  };

  if (!access) {
    return (
      <Section title="MCP">
        <p className="text-[13.5px] text-muted-foreground">{error ? describeError(error) : "Loading…"}</p>
      </Section>
    );
  }
  const config = mcpConfig(access);
  const setupHint = !access.serverPath
    ? "Replace <repo> with the path of the Desk repository."
    : !access.serverBuilt
      ? "Build the server first with pnpm mcp:build."
      : undefined;

  return (
    <Section title="MCP">
      <Row label="Allow agents to use Desk" hint={access.enabled ? undefined : "Agents get an error until you turn this back on."}>
        <Switch
          checked={access.enabled}
          disabled={busy}
          onCheckedChange={(v) => void run(() => api.setMcpAccess(v), "Couldn't change MCP access")}
          aria-label="Allow MCP access"
        />
      </Row>
      <div className="flex flex-col gap-2">
        <Row label="Setup" hint={setupHint}>
          <Button size="sm" variant="outline" onClick={() => void copy(config)}>
            <Copy />
            Copy
          </Button>
        </Row>
        <pre className="whitespace-pre-wrap break-all rounded-md bg-muted px-3 py-2.5 font-mono text-[12px] leading-relaxed text-foreground/85">{config}</pre>
      </div>
      <Row
        label="Token"
        hint={access.rotatedAt ? `Last rotated ${formatTimestamp(access.rotatedAt, localZone())}` : undefined}
      >
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmRotate(true)}>
          Rotate…
        </Button>
      </Row>
      <Dialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <DialogContent className="max-w-[440px]">
          <DialogBody>
            <DialogTitle>Rotate the MCP token?</DialogTitle>
            <DialogDescription>
              The current token stops working now. Agents using this setup pick up the new one on their next request. An agent
              configured with DESK_MCP_TOKEN needs the new value from credentials.json.
            </DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRotate(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={busy}
              onClick={() => {
                setConfirmRotate(false);
                void run(() => api.rotateMcpToken(), "Couldn't rotate the token", "MCP token rotated");
              }}
            >
              Rotate token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
